/**
 * COORDINATE OFFSET SYSTEM
 * 
 * This module prevents coordinate collisions by applying microscopic offsets 
 * to connecting room coordinates during creation. The offsets are so small 
 * they're invisible and don't affect calculations, but prevent exact matches.
 */

import { Point, Wall } from '../types';

// Medium offset value - 0.15 pixels (imperceptible but larger than grid snap precision)
const COORDINATE_OFFSET = 0.15;

// Counter to ensure unique offsets for multiple connecting rooms
let offsetCounter = 0;

export interface OffsetOptions {
  enableOffset?: boolean;
  offsetMultiplier?: number;
  isConnectingRoom?: boolean;
}

export interface OffsetResult {
  offsetApplied: boolean;
  offsetAmount: number;
  reason: string;
}

/**
 * Applies a microscopic offset to a point to prevent coordinate collisions
 */
export function applyCoordinateOffset(
  point: Point, 
  existingWalls: Wall[] = [],
  options: OffsetOptions = {}
): { offsetPoint: Point; result: OffsetResult } {
  const {
    enableOffset = true,
    offsetMultiplier = 1,
    isConnectingRoom = false
  } = options;

  // Return original point if offset is disabled
  if (!enableOffset) {
    return {
      offsetPoint: { ...point },
      result: {
        offsetApplied: false,
        offsetAmount: 0,
        reason: 'Offset disabled'
      }
    };
  }

  // Check if this point would create coordinate collisions
  const hasCoordinateCollision = existingWalls.some(wall => 
    (Math.abs(wall.start.x - point.x) < 0.01 && Math.abs(wall.start.y - point.y) < 0.01) ||
    (Math.abs(wall.end.x - point.x) < 0.01 && Math.abs(wall.end.y - point.y) < 0.01)
  );

  // Only apply offset if we're creating a connecting room or there's a collision
  if (!isConnectingRoom && !hasCoordinateCollision) {
    return {
      offsetPoint: { ...point },
      result: {
        offsetApplied: false,
        offsetAmount: 0,
        reason: 'No collision detected, offset not needed'
      }
    };
  }

  // Increment counter for unique offsets
  offsetCounter = (offsetCounter + 1) % 1000; // Reset after 1000 to prevent overflow
  
  // Apply offset using counter for uniqueness - larger offset to survive snapping
  const baseOffset = COORDINATE_OFFSET * offsetMultiplier;
  const uniqueOffset = baseOffset * (1 + offsetCounter * 0.01); // More significant difference
  const offsetPoint: Point = {
    x: point.x + uniqueOffset,
    y: point.y + uniqueOffset * 0.7 // Slightly different X/Y to ensure no accidental alignment
  };

  console.log('🔬 COORDINATE OFFSET APPLIED:', {
    originalPoint: `(${point.x.toFixed(3)},${point.y.toFixed(3)})`,
    offsetPoint: `(${offsetPoint.x.toFixed(6)},${offsetPoint.y.toFixed(6)})`,
    offsetAmount: uniqueOffset.toFixed(6),
    offsetCounter,
    isConnectingRoom,
    hasCoordinateCollision,
    reason: isConnectingRoom ? 'Connecting room creation' : 'Coordinate collision prevention'
  });

  return {
    offsetPoint,
    result: {
      offsetApplied: true,
      offsetAmount: uniqueOffset,
      reason: isConnectingRoom ? 'Connecting room creation' : 'Coordinate collision prevention'
    }
  };
}

/**
 * Applies coordinate offsets to all walls of a connecting room
 */
export function applyConnectingRoomOffsets(
  walls: Wall[],
  existingWalls: Wall[] = [],
  options: OffsetOptions = {}
): { offsetWalls: Wall[]; offsetResults: OffsetResult[] } {
  const { enableOffset = true } = options;
  
  // Quick return if offsets are disabled
  if (!enableOffset) {
    return {
      offsetWalls: walls.map(w => ({ ...w })),
      offsetResults: walls.map(() => ({
        offsetApplied: false,
        offsetAmount: 0,
        reason: 'Offset disabled'
      }))
    };
  }

  console.log('🏗️ APPLYING CONNECTING ROOM COORDINATE OFFSETS:', {
    wallCount: walls.length,
    existingWallCount: existingWalls.length
  });

  const offsetWalls: Wall[] = [];
  const offsetResults: OffsetResult[] = [];

  for (const wall of walls) {
    // Apply offset to start point
    const startOffset = applyCoordinateOffset(
      wall.start, 
      existingWalls, 
      { ...options, isConnectingRoom: true }
    );

    // Apply offset to end point
    const endOffset = applyCoordinateOffset(
      wall.end, 
      existingWalls, 
      { ...options, isConnectingRoom: true }
    );

    // Create new wall with offset coordinates
    const offsetWall: Wall = {
      ...wall,
      start: startOffset.offsetPoint,
      end: endOffset.offsetPoint
    };

    offsetWalls.push(offsetWall);
    
    // Track if any offset was applied to this wall
    const wallOffsetResult: OffsetResult = {
      offsetApplied: startOffset.result.offsetApplied || endOffset.result.offsetApplied,
      offsetAmount: Math.max(startOffset.result.offsetAmount, endOffset.result.offsetAmount),
      reason: startOffset.result.offsetApplied ? startOffset.result.reason : endOffset.result.reason
    };
    
    offsetResults.push(wallOffsetResult);
  }

  const totalOffsetsApplied = offsetResults.filter(r => r.offsetApplied).length;
  
  console.log('✅ CONNECTING ROOM OFFSET COMPLETE:', {
    wallsProcessed: walls.length,
    offsetsApplied: totalOffsetsApplied,
    maxOffsetAmount: Math.max(...offsetResults.map(r => r.offsetAmount)).toFixed(6)
  });

  return { offsetWalls, offsetResults };
}

/**
 * Detects if a room is being created adjacent to existing rooms
 */
export function isConnectingRoomCreation(
  newRoomWalls: Wall[],
  existingWalls: Wall[],
  proximityThreshold: number = 50
): boolean {
  if (!newRoomWalls.length || !existingWalls.length) {
    return false;
  }

  // Check if any new room wall points are close to existing walls
  for (const newWall of newRoomWalls) {
    const points = [newWall.start, newWall.end];
    
    for (const point of points) {
      for (const existingWall of existingWalls) {
        const distanceToStart = Math.hypot(
          point.x - existingWall.start.x,
          point.y - existingWall.start.y
        );
        const distanceToEnd = Math.hypot(
          point.x - existingWall.end.x,
          point.y - existingWall.end.y
        );
        
        if (distanceToStart < proximityThreshold || distanceToEnd < proximityThreshold) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Smart offset application that only applies offsets when needed
 */
export function applySmartCoordinateOffsets(
  newRoomWalls: Wall[],
  existingWalls: Wall[],
  options: OffsetOptions = {}
): { processedWalls: Wall[]; offsetsApplied: number; isConnecting: boolean } {
  // Detect if this is a connecting room
  const isConnecting = isConnectingRoomCreation(newRoomWalls, existingWalls);
  
  // Only apply offsets for connecting rooms or when explicitly requested
  const shouldApplyOffsets = isConnecting || options.isConnectingRoom;
  
  if (!shouldApplyOffsets) {
    console.log('🔬 SMART OFFSET: No offsets needed (standalone room)');
    return {
      processedWalls: newRoomWalls.map(w => ({ ...w })),
      offsetsApplied: 0,
      isConnecting: false
    };
  }

  // Apply connecting room offsets
  const { offsetWalls, offsetResults } = applyConnectingRoomOffsets(
    newRoomWalls,
    existingWalls,
    { ...options, isConnectingRoom: true }
  );

  const offsetsApplied = offsetResults.filter(r => r.offsetApplied).length;

  console.log('🔬 SMART OFFSET COMPLETE:', {
    isConnecting,
    wallsProcessed: newRoomWalls.length,
    offsetsApplied,
    reason: isConnecting ? 'Connecting room detected' : 'Explicit connecting room'
  });

  return {
    processedWalls: offsetWalls,
    offsetsApplied,
    isConnecting
  };
}